import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight } from "@phosphor-icons/react";

const DEPT_IMAGES = {
  Difusores: "https://images.pexels.com/photos/10924522/pexels-photo-10924522.jpeg",
  Humidificadores: "https://customer-assets.emergentagent.com/job_espanol-lab/artifacts/unaxtluq_New%20Creative%20Home%20Decor%20Crystal%20Hand%20Bag%20USB%20Air%20Humidifier%207%20Color%20Changing%20Night%20Light%20LED%20Humidifier%20Battery%20Rechargeable.jpg",
  Fragancias: "https://images.unsplash.com/photo-1612293905607-b003de9e54fb?crop=entropy&cs=srgb&fm=jpg&q=85",
  Joyería: "https://customer-assets.emergentagent.com/job_espanol-lab/artifacts/ag4piaxc_FEIRUN%208mm%203A%20Drop%2018K%20Real%20Genuine%20Yellow%20Gold%20Natural%20Real%20Pearl%20Jewelry%20Set%20Freshwater%20Pearl%20Set%20for%20Women.jpg",
  Hogar: "https://images.unsplash.com/photo-1613521140785-e85e427f8002?crop=entropy&cs=srgb&fm=jpg&q=85",
  Decoración: "https://customer-assets.emergentagent.com/job_espanol-lab/artifacts/1yvieh1b_Hot%20Selling%20Creative%20LED%20Glowing%20Acrylic%20Handbag%20Candle%20Light-Up%20Fashion%20Design%20Bag%20for%20Birthday%20%26%20Christmas%20Holiday.png",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [onSale, setOnSale] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/products/best-sellers", { params: { limit: 6 } }).then((r) => setBestSellers(r.data)).catch(() => {});
    api.get("/products", { params: { on_sale: true } }).then((r) => setOnSale(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[78vh] min-h-[520px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1612293905607-b003de9e54fb?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Luxe Aroma Scents"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/70 via-stone-900/30 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 h-full flex flex-col justify-center">
          <div className="max-w-xl fade-up">
            <div className="label-caps text-[#C0A062] mb-4">Luxe Aroma Scents</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.05]">
              Aromas que transforman tu hogar
            </h1>
            <p className="text-stone-200 mt-6 text-lg leading-relaxed">
              Fragancias, velas y piezas de decoración seleccionadas con esmero para cada espacio.
            </p>
            <Link to="/shop" className="inline-flex items-center gap-2 mt-8 bg-white text-stone-900 px-8 py-4 hover:bg-stone-100 transition-colors" data-testid="hero-shop-button">
              Explorar la tienda <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-caps text-[#C0A062] mb-2">Departamentos</div>
            <h2 className="font-serif text-3xl md:text-4xl text-stone-900">Compra por categoría</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {departments.map((d, i) => (
            <Link key={d} to={`/shop?department=${encodeURIComponent(d)}`} className="group relative overflow-hidden aspect-[3/4] bg-stone-200 fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`dept-tile-${d}`}>
              <img src={DEPT_IMAGES[d] || DEPT_IMAGES.Fragancias} alt={d} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="font-serif text-3xl text-white">{d}</h3>
                <span className="label-caps text-stone-200 flex items-center gap-2 mt-1">Ver productos <ArrowRight size={14} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Venta / Ofertas */}
      {onSale.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8" data-testid="home-venta-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-caps text-[#991B1B] mb-2">Ofertas</div>
              <h2 className="font-serif text-3xl md:text-4xl text-stone-900">Venta — Precios rebajados</h2>
            </div>
            <Link to="/shop?on_sale=true" className="label-caps text-stone-600 hover:text-stone-900 flex items-center gap-2">Ver todas <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {onSale.slice(0, 6).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Best sellers */}
      {bestSellers.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16" data-testid="home-bestsellers-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-caps text-[#C0A062] mb-2">Los favoritos</div>
              <h2 className="font-serif text-3xl md:text-4xl text-stone-900">Productos más vendidos</h2>
            </div>
            <Link to="/shop" className="label-caps text-stone-600 hover:text-stone-900 flex items-center gap-2">Ver todo <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {bestSellers.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-caps text-[#C0A062] mb-2">Selección</div>
              <h2 className="font-serif text-3xl md:text-4xl text-stone-900">Productos destacados</h2>
            </div>
            <Link to="/shop" className="label-caps text-stone-600 hover:text-stone-900 flex items-center gap-2">Ver todo <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
